import { FilterGroup, FilterCondition } from 'types/userConfig/userConfig';
import { mapFieldToApiField } from './field-mapping';
import { parseRelativeLastDynamic, resolveDynamicValue } from './resolve-dynamic-values';
import { tagNameFromLabelFilterToken } from './conversation-tag-filter';
import dayjs from 'dayjs';

/**
 * שדות תגית legacy (תגית איש קשר / lastSession.label) — מוסבים לשדה conversationTag
 * של מנוע הסינון בנוד. הערכים בתצוגות שמורות הם JSON.stringify(ChatUserLabel);
 * ממירים לשם התגית עצמו.
 */
const LEGACY_LABEL_API_FIELDS = new Set(['label', 'lastSession.label']);

function convertLegacyLabelValueToTagName(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const name = tagNameFromLabelFilterToken(value);
  //* 'noLabel' — נשאר כערך המקורי; המפעיל (isnull) לרוב לא צורך ערך בכלל
  return name === null ? value : name;
}

/**
 * ממיר FilterGroup[] למבנה API שצפוי על ידי השרת
 */
export function convertFilterGroupsToApiFilters(filterGroups: FilterGroup[]): any[] {
  if (!filterGroups || filterGroups.length === 0) {
    return [];
  }

  return filterGroups.map((group) => {
    const conditions = group.conditions
      .filter((cond) => cond.field && cond.operator) // רק תנאים תקינים
      .flatMap((cond) => {
        // המרת שדה לשדה API
        let apiField = mapFieldToApiField(cond.field);

        //* תגית legacy → תגיות שיחה: השדה הופך ל-conversationTag והערך לשם התגית.
        //* תצוגות שמורות (LableName / lastSession.label עם ערכי JSON) ממשיכות לעבוד.
        let isConversationTagField = false;
        if (LEGACY_LABEL_API_FIELDS.has(apiField)) {
          apiField = 'conversationTag';
          isConversationTagField = true;
        }

        // טיפול בשדות From/To - המרת operator לפי הסיומת
        //* string (ולא ה-union של FilterCondition) — האופרטורים בצד ה-API רחבים יותר (למשל notcontains)
        let finalOperator: string = cond.operator;
        let finalField = apiField;

        //* conversationTag בנוד הוא אגרגט STRING_AGG ('tag1||tag2') — "יש תגית X"
        //* דורש contains (eq משווה את כל המחרוזת ונכשל בריבוי תגיות)
        if (isConversationTagField) {
          if (finalOperator === 'eq') finalOperator = 'contains';
          else if (finalOperator === 'ne') finalOperator = 'notcontains';
        }
        
        // אם השדה מסתיים ב-From, נשתמש ב-gte (greater than or equal) או gt
        // אם השדה מסתיים ב-To, נשתמש ב-lte (less than or equal) או lt
        if (cond.field.endsWith('From')) {
          // עבור From, נשתמש ב-gte אם operator הוא eq, אחרת gt
          finalOperator = cond.operator === 'eq' ? 'gte' : 'gt';
        } else if (cond.field.endsWith('To')) {
          // עבור To, נשתמש ב-lte אם operator הוא eq, אחרת lt
          finalOperator = cond.operator === 'eq' ? 'lte' : 'lt';
        }

        // קבע את סוג השדה להמרת ערכים דינמיים
        let fieldType: 'dateTime' | 'manager' | 'other' = 'other';
        if (finalField.includes('date') || finalField.includes('Date') || finalField.includes('Time')) {
          fieldType = 'dateTime';
        } else if (finalField === 'managerId') {
          fieldType = 'manager';
        }

        // המר ערכים דינמיים לערכים אמיתיים
        let resolvedValue = cond.value;
        if (cond.value !== undefined && cond.value !== null) {
          if (Array.isArray(cond.value)) {
            resolvedValue = cond.value.map(v => resolveDynamicValue(v, fieldType));
          } else {
            resolvedValue = resolveDynamicValue(cond.value, fieldType);
          }
        }

        //* ערכי תגית legacy (JSON של ChatUserLabel) → שם תגית שיחה
        if (isConversationTagField && resolvedValue !== undefined && resolvedValue !== null) {
          resolvedValue = Array.isArray(resolvedValue)
            ? resolvedValue.map(convertLegacyLabelValueToTagName)
            : convertLegacyLabelValueToTagName(resolvedValue);
        }

        // ערך יחסי "ב-X האחרונים" (דקות/שעות/ימים): "שווה ל" פירושו הטבעי "בתוך
        // התקופה" → gte מהרגע שנפתר; "שונה מ" → lt (לפני התקופה). בלי זה eq היה
        // קורס לטווח יום בודד של לפני X — חסר משמעות למשתמש.
        const isRelativeLastValue = fieldType === 'dateTime' && parseRelativeLastDynamic(cond.value) !== null;
        if (isRelativeLastValue && !cond.field.endsWith('From') && !cond.field.endsWith('To')) {
          if (finalOperator === 'eq') finalOperator = 'gte';
          else if (finalOperator === 'ne') finalOperator = 'lt';
        }

        // eq על שדה dateTime עם ערך מפורש => פיצול לטווח לפי הדיוק שנבחר:
        // חצות מדויקת (בורר תאריך בלי שעה / "היום"/"אתמול") → יום שלם, כמו תמיד;
        // ערך עם שעה → טווח של דקה אחת, כך שהשעה שנבחרה באמת משפיעה.
        if (
          finalOperator === 'eq' &&
          fieldType === 'dateTime' &&
          !cond.field.endsWith('From') &&
          !cond.field.endsWith('To') &&
          typeof resolvedValue === 'string' &&
          isIsoDateString(resolvedValue)
        ) {
          const picked = dayjs(resolvedValue);
          const isMidnight = picked.hour() === 0 && picked.minute() === 0 && picked.second() === 0;
          const rangeStart = isMidnight ? picked.startOf('day') : picked.startOf('minute');
          const rangeEnd = isMidnight ? rangeStart.add(1, 'day') : rangeStart.add(1, 'minute');
          return [
            { field: finalField, operator: 'gte', value: rangeStart.toISOString() },
            { field: finalField, operator: 'lt', value: rangeEnd.toISOString() },
          ];
        }
        
        const apiCondition: any = {
          field: finalField,
          operator: finalOperator,
        };

        // הוסף value רק אם הוא נדרש (לא עבור isnull/isnotnull)
        if (finalOperator !== 'isnull' && finalOperator !== 'isnotnull') {
          if (resolvedValue !== undefined && resolvedValue !== null) {
            // עבור operators 'eq' ו-'ne', הערך צריך להיות string (לא array)
            // עבור operators 'in' ו-'notin', הערך צריך להיות array
            if ((finalOperator === 'eq' || finalOperator === 'ne') && Array.isArray(resolvedValue)) {
              apiCondition.value = resolvedValue.length > 0 ? resolvedValue[0] : null;
            } else if ((finalOperator === 'in' || finalOperator === 'notin') && !Array.isArray(resolvedValue)) {
              apiCondition.value = [resolvedValue];
            } else {
              apiCondition.value = resolvedValue;
            }
          }
        }

        return [apiCondition];
      });

    // אם אין תנאים תקינים, החזר null (ייסונן אחר כך)
    if (conditions.length === 0) {
      return null;
    }

    const result: any = {
      conditions,
    };

    // הוסף logic רק אם זה 'or' (ברירת מחדל היא 'and')
    if (group.logic === 'or') {
      result.logic = 'or';
    }

    return result;
  }).filter((filter) => filter !== null); // הסר nulls
}

/**
 * ממיר את הלוגיקה בין קבוצות למבנה API
 * אם filtersLogic הוא 'or', צריך לשלוח את כל הקבוצות עם logic: 'or' ביניהן
 * אם filtersLogic הוא 'and' (ברירת מחדל), כל קבוצה נשלחת בנפרד
 */
export function convertCustomFiltersToApiFilters(
  filterGroups: FilterGroup[],
  filtersLogic?: 'or' | 'and',
): any[] {
  const apiFilters = convertFilterGroupsToApiFilters(filterGroups);

  if (apiFilters.length === 0) {
    return [];
  }

  // אם יש רק קבוצה אחת או logic הוא 'and', החזר את כל הקבוצות בנפרד
  if (apiFilters.length === 1 || filtersLogic === 'and' || !filtersLogic) {
    return apiFilters;
  }

  // אם logic הוא 'or', צריך לשלוח את כל הקבוצות עם logic: 'or' ביניהן
  // אבל לפי המבנה של השרת, כל קבוצה היא filter נפרד, אז נשלח אותן בנפרד
  // השרת יבין שהן OR לפי הסדר או לפי מבנה אחר
  // כרגע נשלח אותן בנפרד - השרת צריך לטפל ב-logic בין filters
  
  // אם השרת מצפה למבנה עם logic ברמה העליונה, צריך לשלוח כך:
  // { logic: 'or', conditions: [...] } - אבל זה לא תואם למבנה הנוכחי
  
  // כרגע נשלח כל קבוצה בנפרד - השרת צריך לטפל ב-logic בין filters
  return apiFilters;
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
function isIsoDateString(value: string): boolean {
  return ISO_DATE_RE.test(value);
}

